import { IconCopy, IconWorldUpload } from "@tabler/icons-react"
import { type FC } from "react"

import routes from "@forento/core/routes"

import { DeleteIcon, EditIcon, LinkIcon, PreviewIcon, SettingsIcon } from "~/components/Icon"
import ProductListRow from "~/components/ProductListRow"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import useProductPublishedAlert from "~/hooks/useProductPublishedAlert"
import trpc, { query } from "~/utilities/trpc"

type Props = { course: { id: number; title: string }; reload(): Promise<unknown> }
const DraftCourse: FC<Props> = ({ course: { id, title }, reload }) => {
	const user = useUser()
	const alert = useAlert()
	const publishedAlert = useProductPublishedAlert("course")

	const duplicate = query.course.duplicate.useMutation({ onSuccess: reload, onError: error => console.error(error) })
	const preview = query.user.createLoginToken.useMutation({
		onSuccess: ({ url }) => window.open(url, "_blank"),
		onError: error => console.error(error),
	})

	async function handleDuplicate() {
		const result = await alert.confirm(
			"Duplicate course",
			`Are you sure you want to duplicate the course **${title}**?`,
			{ isDestructive: false },
		)
		if (!result.result) return
		await duplicate.mutateAsync(id)
		result.close()
	}

	async function handlePublish() {
		const result = await alert.confirm(
			"Publish course",
			`Are you sure you want to publish the course **${title}**?`,
			{ isDestructive: false },
		)
		if (!result.result) return
		await trpc.course.publish.mutate(id)
		await reload()
		result.close()
		await publishedAlert.show({ itemId: id })
	}

	async function handleDelete() {
		const result = await alert.confirm(
			"Delete course",
			`Are you sure you want to delete the course **${title}**? This action is irreversible.`,
		)
		if (!result.result) return
		await trpc.course.deleteById.mutate(id)
		await reload()
		result.close()
	}

	return (
		<ProductListRow
			title={title}
			subtitle="Not published"
			href={routes.app.course.detail(id)}
			actions={[
				{ id: "edit", tooltip: "Edit", icon: EditIcon, href: routes.app.course.detail(id) },
				{ id: "settings", tooltip: "Settings", icon: SettingsIcon, href: routes.app.course.settings(id) },
				{
					id: "open-link",
					tooltip: "Open link",
					icon: LinkIcon,
					href: `//${user.user!.platform!.domain.platform}${routes.platform.course.detail(id)}`,
				},
				{
					id: "preview",
					tooltip: "Preview",
					icon: PreviewIcon,
					onClick: () => preview.mutate({ platformPath: routes.platform.course.player(id) }),
				},
				{ id: "duplicate", tooltip: "Duplicate", icon: IconCopy, onClick: handleDuplicate },
				{ id: "publish", tooltip: "Publish", icon: IconWorldUpload, onClick: handlePublish },
				{ id: "delete", tooltip: "Delete", icon: DeleteIcon, danger: true, onClick: handleDelete },
			]}
		/>
	)
}

export default DraftCourse
